/*
 * Mobile sticky add-to-cart bar.
 *
 * Proxies the main product form so the theme's existing variant/availability/
 * cart logic keeps working. The bar lets the customer pick a size, change the
 * quantity and add the product to the cart from a bottom-fixed bar on mobile.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.productFormId = this.dataset.productFormId;
        this.productInfo = this.closest('product-info');

        this.qtyInput = this.querySelector('.sticky-atc__qty-input');
        this.addButton = this.querySelector('[data-sticky-atc-add]');
        this.addButtonText = this.querySelector('.sticky-atc__add-text');

        this.removeAttribute('hidden');

        this.bindSizes();
        this.bindQuantity();
        this.bindAdd();
        this.setupVisibilityObserver();
        this.syncFromMain();

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
            const changedSection = payload?.data?.sectionId;
            if (changedSection && changedSection !== this.sectionId) return;
            this.syncFromMain();
            // Release the scroll lock shortly after the post-fetch focus fires.
            setTimeout(() => this.unlockScroll(), 350);
          });
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.resetQuantity());
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.syncQuantityBounds());
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
        this.visibilityObserver?.disconnect();
        clearTimeout(this.scrollLockTimeout);
        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler, true);
          this.scrollHandler = null;
        }
      }

      /* ---------------------------------------------------------------- Size */

      bindSizes() {
        this.sizeSelect = this.querySelector('.sticky-atc__size-select');
        this.sizeSelect?.addEventListener('change', () => this.selectSize());
      }

      selectSize() {
        if (!this.sizeSelect) return;
        const target = document.getElementById(this.sizeSelect.value);
        if (!target) return;

        // Keep the viewport in place: the theme focuses the main variant input
        // after the variant fetch, which would otherwise scroll the page away
        // from the sticky bar.
        this.lockScroll();

        if (target.tagName === 'OPTION') {
          const select = target.closest('select');
          if (!select) return;
          select.value = target.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      syncSizes() {
        if (!this.sizeSelect) return;

        Array.from(this.sizeSelect.options).forEach((option) => {
          const target = document.getElementById(option.value);
          if (!target) return;

          const selected = target.tagName === 'OPTION' ? target.hasAttribute('selected') : target.checked;
          if (selected) this.sizeSelect.value = option.value;
        });
      }

      lockScroll() {
        this.scrollLockY = window.scrollY;

        if (!this.scrollHandler) {
          this.scrollHandler = () => {
            if (this.scrollLockY !== null && this.scrollLockY !== undefined) {
              window.scrollTo(window.scrollX, this.scrollLockY);
            }
          };
          window.addEventListener('scroll', this.scrollHandler, true);
        }

        // Safety release in case no variant-change event follows (e.g. the
        // selected combination becomes unavailable).
        clearTimeout(this.scrollLockTimeout);
        this.scrollLockTimeout = setTimeout(() => this.unlockScroll(), 1200);
      }

      unlockScroll() {
        this.scrollLockY = null;
        clearTimeout(this.scrollLockTimeout);
      }

      /* ------------------------------------------------------------ Quantity */

      bindQuantity() {
        this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
        this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.changeQuantity(1));
        this.qtyInput?.addEventListener('change', () => this.clampQuantity());
      }

      changeQuantity(direction) {
        if (!this.qtyInput) return;
        const step = parseInt(this.qtyInput.step, 10) || 1;
        const current = parseInt(this.qtyInput.value, 10) || step;
        const max = this.qtyInput.max ? parseInt(this.qtyInput.max, 10) : null;

        if (
          direction > 0 &&
          max !== null &&
          current >= max &&
          this.mainQuantityInput?.hasAttribute('data-purchase-max')
        ) {
          if (typeof showPurchaseLimitWarning === 'function') {
            showPurchaseLimitWarning(this.querySelector('[data-qty-plus]') || this.qtyInput);
          }
          return;
        }

        this.qtyInput.value = current + direction * step;
        this.clampQuantity(true);
      }

      clampQuantity(notify) {
        if (!this.qtyInput) return;
        const min = parseInt(this.qtyInput.min, 10) || 1;
        const max = this.qtyInput.max ? parseInt(this.qtyInput.max, 10) : null;
        let value = parseInt(this.qtyInput.value, 10) || min;
        const exceeded = max !== null && value > max;
        if (value < min) value = min;
        if (exceeded) value = max;
        this.qtyInput.value = value;

        if (exceeded && notify && this.mainQuantityInput?.hasAttribute('data-purchase-max')) {
          if (typeof showPurchaseLimitWarning === 'function') {
            showPurchaseLimitWarning(this.querySelector('[data-qty-plus]') || this.qtyInput);
          }
        }
      }

      resetQuantity() {
        if (!this.qtyInput) return;
        this.qtyInput.value = parseInt(this.qtyInput.min, 10) || 1;
      }

      syncQuantityBounds() {
        if (!this.qtyInput) return;
        const mainInput = this.mainQuantityInput;
        if (mainInput) {
          if (mainInput.min) this.qtyInput.min = mainInput.min;
          if (mainInput.max) {
            this.qtyInput.max = mainInput.max;
          } else {
            this.qtyInput.removeAttribute('max');
          }
          this.qtyInput.step = mainInput.step || 1;
        }
        this.clampQuantity();
      }

      /* ----------------------------------------------------------- Add to cart */

      bindAdd() {
        this.addButton?.addEventListener('click', () => this.addToCart());
      }

      addToCart() {
        if (!this.addButton || this.addButton.disabled) return;

        const quantity = parseInt(this.qtyInput?.value, 10) || 1;
        const form = document.getElementById(this.productFormId);
        const mainQuantity = this.mainQuantityInput;

        if (mainQuantity) {
          mainQuantity.value = quantity;
          mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (form) {
          let hidden = form.querySelector('input[name="quantity"]');
          if (!hidden) {
            hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'quantity';
            form.appendChild(hidden);
          }
          hidden.value = quantity;
        }

        const mainSubmit = this.mainSubmitButton;
        if (mainSubmit) {
          mainSubmit.click();
        } else if (form) {
          if (form.requestSubmit) {
            form.requestSubmit();
          } else {
            form.submit();
          }
        }
      }

      /* ---------------------------------------------------------------- Sync */

      syncFromMain() {
        this.syncSizes();
        this.syncButton();
        this.syncPrice();
        this.syncQuantityBounds();
      }

      syncButton() {
        const mainSubmit = this.mainSubmitButton;
        if (!mainSubmit || !this.addButton) return;

        const disabled =
          mainSubmit.hasAttribute('disabled') || mainSubmit.getAttribute('aria-disabled') === 'true';
        this.addButton.disabled = disabled;

        const label = mainSubmit.querySelector('span');
        if (label && this.addButtonText) {
          const text = label.textContent.trim();
          if (text) this.addButtonText.textContent = text;
        }
      }

      syncPrice() {
        const main = document.getElementById(`price-${this.sectionId}`);
        const slot = document.getElementById(`StickyPrice-${this.sectionId}`);
        if (main && slot) slot.innerHTML = main.innerHTML;
      }

      /* ---------------------------------------------------------- Visibility */

      setupVisibilityObserver() {
        const target = this.mainSubmitButton || this.productInfo?.querySelector('.product-form__buttons');
        if (!target) {
          this.classList.add('is-visible');
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle('is-visible', !entry.isIntersecting);
            });
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(target);
      }

      /* ------------------------------------------------------------- Getters */

      get mainSubmitButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      get mainQuantityInput() {
        return document.getElementById(`Quantity-${this.sectionId}`);
      }
    }
  );
}
